import { useState } from 'react';
import { X, DollarSign, FileText, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useGlobalToast } from '@/contexts/ToastContext';

interface CompleteJobModalProps {
  jobTitle: string;
  quotedAmount?: number;
  onClose: () => void;
  onComplete: (data: CompleteJobData) => Promise<void>;
}

export interface CompleteJobData {
  finalAmount: number;
  workDescription: string;
  paymentDetails?: string;
}

export function CompleteJobModal({ 
  jobTitle, 
  quotedAmount, 
  onClose, 
  onComplete 
}: CompleteJobModalProps) {
  const { success, error } = useGlobalToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<CompleteJobData>({
    finalAmount: quotedAmount || 0,
    workDescription: '',
    paymentDetails: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.finalAmount || formData.finalAmount <= 0) {
      newErrors.finalAmount = 'Please enter a valid amount';
    }

    if (!formData.workDescription.trim()) {
      newErrors.workDescription = 'Please describe the work completed';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      error('Validation Error', 'Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await onComplete(formData);
      success('Job Completed', 'Invoice has been sent to the customer');
      onClose();
    } catch (err: any) {
      error('Error', err.message || 'Failed to complete job');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-in fade-in duration-300">
      <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl animate-in zoom-in duration-300">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Complete Job</h2>
            <p className="text-sm text-gray-600 mt-1">{jobTitle}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Final Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <DollarSign className="w-4 h-4 inline mr-1" />
              Final Amount (€) *
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.finalAmount || ''}
              onChange={(e) => {
                setFormData({ ...formData, finalAmount: parseFloat(e.target.value) || 0 });
                if (errors.finalAmount) setErrors({ ...errors, finalAmount: '' });
              }}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 text-lg font-semibold ${
                errors.finalAmount ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="0.00"
            />
            {errors.finalAmount && (
              <p className="text-xs text-red-600 mt-1">{errors.finalAmount}</p>
            )}
            {quotedAmount && quotedAmount !== formData.finalAmount && (
              <p className="text-xs text-gray-500 mt-1">
                Original quote: €{quotedAmount.toFixed(2)}
              </p>
            )}
          </div>

          {/* Work Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="w-4 h-4 inline mr-1" />
              Work Completed *
            </label>
            <textarea
              value={formData.workDescription}
              onChange={(e) => {
                setFormData({ ...formData, workDescription: e.target.value });
                if (errors.workDescription) setErrors({ ...errors, workDescription: '' });
              }}
              rows={4}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                errors.workDescription ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Describe the work you completed (e.g., Fixed leaking pipe, replaced valve, tested system)"
            />
            {errors.workDescription && (
              <p className="text-xs text-red-600 mt-1">{errors.workDescription}</p>
            )}
          </div>

          {/* Payment Details (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <CreditCard className="w-4 h-4 inline mr-1" />
              Payment Instructions (Optional)
            </label>
            <textarea
              value={formData.paymentDetails}
              onChange={(e) => setFormData({ ...formData, paymentDetails: e.target.value })}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Bank transfer details, payment methods accepted, etc."
            />
            <p className="text-xs text-gray-500 mt-1">
              This will be included in the invoice sent to the customer
            </p>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-900">
              <strong>What happens next:</strong>
            </p>
            <ul className="text-sm text-blue-800 mt-2 space-y-1 ml-4 list-disc">
              <li>Job will be marked as completed</li>
              <li>Invoice will be automatically generated</li>
              <li>Customer will receive invoice via email and in-app notification</li>
              <li>You can track payment status in your invoices page</li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              fullWidth
            >
              {isSubmitting ? 'Processing...' : 'Complete Job & Send Invoice'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
